<script setup lang="ts">
import { HomeKpiGrid, HomeQuickLinks, TraceIdAlert, useSession, type KpiCard, type QuickLink } from '@yc/ui-kit'
import { onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'

import { getHomeStats, type HomeStats } from '../api/home'

const { me } = useSession()
const router = useRouter()

const loading = ref(false)
const error = ref<unknown>(null)

const greeting = () => {
  const h = new Date().getHours()
  if (h < 6) return '夜深了'
  if (h < 12) return '早上好'
  if (h < 14) return '中午好'
  if (h < 18) return '下午好'
  return '晚上好'
}

/** 卡片标签：后端 JSON 字段 → 页面文案 */
const defs: Array<{ key: keyof HomeStats; label: string }> = [
  { key: 'pending_tasks', label: '待调度任务' },
  { key: 'urgent_pending', label: '其中加急' },
  { key: 'idle_vehicles', label: '空闲车辆' },
  { key: 'planned_trips', label: '待发车任务' },
  { key: 'running_trips', label: '在途任务' },
  { key: 'today_trips', label: '今日新建任务' },
]

const cards = ref<KpiCard[]>(
  defs.map((d) => ({ key: d.key, label: d.label, value: '—' })),
)

const links: QuickLink[] = [
  { to: '/dispatch', label: '调度中心', perm: 'dsp:task' },
  { to: '/trips', label: '行车任务', perm: 'dsp:trip' },
  { to: '/vehicles', label: '车辆管理', perm: 'dsp:vehicle' },
  { to: '/drivers', label: '司机管理', perm: 'dsp:driver' },
  { to: '/routes', label: '线路管理', perm: 'dsp:route' },
]

onMounted(async () => {
  loading.value = true
  error.value = null
  try {
    const s = await getHomeStats()
    cards.value = defs.map((d) => ({ key: d.key, label: d.label, value: s[d.key] ?? '—' }))
  } catch (e) {
    error.value = e
  } finally {
    loading.value = false
  }
})
</script>

<template>
  <div class="yc-home">
    <header class="yc-home__hero">
      <h2 class="yc-home__greeting">{{ greeting() }}，{{ me?.name || me?.username }}</h2>
      <p class="yc-home__sub">
        {{ me?.tenant?.name }}（{{ me?.tenant?.code }}）· {{ me?.username }}
      </p>
    </header>

    <div v-if="error" class="yc-home__error">
      <TraceIdAlert :error="error" />
    </div>

    <HomeKpiGrid :cards="cards" :loading="loading" />

    <HomeQuickLinks :links="links" @navigate="router.push" />
  </div>
</template>

<style scoped lang="scss">
.yc-home {
  &__hero {
    margin-bottom: var(--yc-space-6);
  }

  &__greeting {
    margin: 0;
    font-size: var(--yc-font-2xl);
    font-weight: 600;
  }

  &__sub {
    margin: var(--yc-space-2) 0 0;
    color: var(--yc-text-secondary);
    font-size: var(--yc-font);
  }

  &__error {
    margin-bottom: var(--yc-space-3);
    font-size: var(--yc-font-sm);
  }
}
</style>
